import React from "react";
import { Box, Typography } from "@mui/material";
import { AccessTime, Add, HourglassTop, Notifications, TrendingUp, Visibility } from "@mui/icons-material";
import { ListingCellCenter } from "../../../components/commonComponents/TabularLists/styles";
import { requestAnalyticsReport } from "../../../api_calls/campaigns";
import { canEditAcrossCompanies } from "../../../api_calls/crossCompanyAccess";
import ReportStateButton, {
  COMBINED_REPORTS_WIDTH,
  IMPRESSIONS_BUTTON_WIDTH,
  PERFORMANCE_BUTTON_WIDTH,
  REPORT_BUTTON_GAP,
} from "./ReportStateButton";

// Same product-gate as the Action menu checkmark: codes 0 and 1 mean no analytics products yet.
const hasAnalyticsProducts = (campaign) => {
  return ![0, 1].includes(campaign.campaign_status_code);
};

// Brand always sees performance. Others only see it for campaigns they own, unless they can edit across companies.
const canActOnPerformance = (campaign, userInfo, userRole) => {
  const isBrandRole =
    typeof userRole.user_role !== "undefined" && userRole.user_role.toLowerCase() === "brand";

  if (isBrandRole === true) {
    return true;
  }

  return (
    typeof userInfo.current_workspace !== "undefined" &&
    (userInfo.current_workspace.name === campaign.owner_company_name || canEditAcrossCompanies(userInfo.user_type) === true)
  );
};

// Scheduled campaigns (and leftover status 5) have no report buttons.
const isReportsHiddenBySchedule = (campaign) => {
  return campaign.campaign_status === "Scheduled" || campaign.campaign_status_code === 5;
};

const getImpressionsState = (campaign, impressionSectionAvailable) => {
  if (isReportsHiddenBySchedule(campaign) === true) {
    return null;
  }

  if (impressionSectionAvailable === true && campaign.impression_report_status === 1) {
    return "available";
  }

  if (impressionSectionAvailable === false || campaign.impression_report_status === 0) {
    return "preparing";
  }

  return null;
};

const getPerformanceState = (campaign, performanceAvailable) => {
  if (isReportsHiddenBySchedule(campaign) === true) {
    return null;
  }

  if (hasAnalyticsProducts(campaign) !== true) {
    return "addProducts";
  }

  if (campaign.campaign_status_code === 3) {
    return "request";
  }

  if (campaign.campaign_status_code === 2) {
    return "requested";
  }

  if (campaign.campaign_status_code === 4 && performanceAvailable !== true) {
    return "requested";
  }

  if (campaign.campaign_status_code === 4 && performanceAvailable === true) {
    return "available";
  }

  return null;
};

const openAnalytics = (navigate, campaign) => {
  navigate("/check-campaigns/campaign-analytics/" + campaign.campaign_id);
};

const CampaignReportButtons = ({
  campaign,
  canSeeImpressions,
  canSeePerformance,
  impressionSectionAvailable,
  performanceAvailable,
  canRequestPerformance,
  userInfo,
  userRole,
  navigate,
}) => {
  const impressionsState =
    canSeeImpressions === true ? getImpressionsState(campaign, impressionSectionAvailable) : null;

  const performanceState =
    canSeePerformance === true && canActOnPerformance(campaign, userInfo, userRole) === true
      ? getPerformanceState(campaign, performanceAvailable)
      : null;

  // Both ready: one Full Report button that spans the two-button width.
  if (impressionsState === "available" && performanceState === "available") {
    return (
      <ListingCellCenter>
        <ReportStateButton
          title="Full Report"
          status="Both Ready"
          variant="lightRed"
          icon={<TrendingUp sx={{ color: "#fff" }} />}
          width={COMBINED_REPORTS_WIDTH}
          ariaText={"report available for " + campaign.campaign_name}
          tooltip="Open the full report"
          onClick={() => openAnalytics(navigate, campaign)}
        />
      </ListingCellCenter>
    );
  }

  const impressionsButton =
    impressionsState === "available" ? (
      <ReportStateButton
        title="Impressions"
        status="Available"
        variant="solid"
        icon={<Visibility sx={{ color: "#fff" }} />}
        width={IMPRESSIONS_BUTTON_WIDTH}
        ariaText={"impressions available for " + campaign.campaign_name}
        tooltip="Open the impressions report"
        onClick={() => openAnalytics(navigate, campaign)}
      />
    ) : impressionsState === "preparing" ? (
      <ReportStateButton
        title="Impressions"
        status="Preparing"
        variant="outlined"
        icon={<AccessTime sx={{ color: "#AF3241" }} />}
        width={IMPRESSIONS_BUTTON_WIDTH}
        ariaText={"impressions awaited for " + campaign.campaign_name}
        tooltip="Impressions report is being prepared"
        disabled={true}
      />
    ) : null;

  let performanceButton = null;

  if (performanceState === "addProducts") {
    performanceButton = (
      <ReportStateButton
        title="Performance"
        status="Add Products"
        variant="outlined"
        icon={<Add sx={{ color: "#AF3241" }} />}
        width={PERFORMANCE_BUTTON_WIDTH}
        ariaText={"report can not be requested for " + campaign.campaign_name}
        tooltip="Add analytics products before the performance report can be requested"
        disabled={true}
      />
    );
  } else if (performanceState === "request") {
    performanceButton = (
      <ReportStateButton
        title="Performance"
        status="Request"
        variant="dark"
        icon={<Notifications sx={{ color: "#fff" }} />}
        width={PERFORMANCE_BUTTON_WIDTH}
        ariaText={"report can be requested for " + campaign.campaign_name}
        tooltip={canRequestPerformance === true ? "Request the performance report" : "No access"}
        disabled={canRequestPerformance !== true}
        onClick={() => {
          if (canRequestPerformance === true) {
            requestAnalyticsReport(campaign.campaign_id, campaign.campaign_name, campaign.owner_company_id);
          }
        }}
      />
    );
  } else if (performanceState === "requested") {
    performanceButton = (
      <ReportStateButton
        title="Performance"
        status="Requested"
        variant="grey"
        icon={<HourglassTop sx={{ color: "#fff" }} />}
        width={PERFORMANCE_BUTTON_WIDTH}
        ariaText={"report requested for " + campaign.campaign_name}
        tooltip="Report was requested and is being prepared"
        disabled={true}
      />
    );
  } else if (performanceState === "available") {
    performanceButton = (
      <ReportStateButton
        title="Performance"
        status="Available"
        variant="solid"
        icon={<TrendingUp sx={{ color: "#fff"}} />}
        width={PERFORMANCE_BUTTON_WIDTH}
        ariaText={"report available for " + campaign.campaign_name}
        tooltip="Open the performance report"
        onClick={() => openAnalytics(navigate, campaign)}
      />
    );
  }

  // Only one of the two reports applies — stretch that button to the combined width.
  const onlyImpressions = impressionsButton !== null && performanceButton === null;
  const onlyPerformance = impressionsButton === null && performanceButton !== null;

  if (onlyImpressions === true) {
    return (
      <ListingCellCenter>
        {React.cloneElement(impressionsButton, { width: COMBINED_REPORTS_WIDTH })}
      </ListingCellCenter>
    );
  }

  if (onlyPerformance === true) {
    return (
      <ListingCellCenter>
        {React.cloneElement(performanceButton, { width: COMBINED_REPORTS_WIDTH })}
      </ListingCellCenter>
    );
  }

  if (impressionsButton === null && performanceButton === null) {
    return (
      <ListingCellCenter>
        <Typography variant="body2">–</Typography>
      </ListingCellCenter>
    );
  }

  return (
    <ListingCellCenter>
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: `${REPORT_BUTTON_GAP}px` }}>
        {impressionsButton}
        {performanceButton}
      </Box>
    </ListingCellCenter>
  );
};

export default CampaignReportButtons;
